import { useState, useEffect, useRef } from "react";

import { Link, useNavigate } from "react-router-dom";
import { CheckCircle, Mail, Clock, MessageCircle, ChevronRight, PhoneCall } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { trackFormEvent } from "@/lib/formTracking";
import BookCallModal from "@/components/BookCallModal";

const FORM_NAME = "Support Enquiry";

const inputStyle = {
  border: "1.5px solid #E5E5E5",
  borderRadius: 10,
  padding: "13px 16px",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 15,
  color: "#1A1A1A",
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.2s",
};

const labelStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#888",
  display: "block",
  marginBottom: 6,
};

export default function Support() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [showBookCall, setShowBookCall] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const hasSubmittedRef = useRef(false);
  const hasStartedRef = useRef(false);

  // Track a drop-off if the visitor started the form but leaves without submitting
  useEffect(() => {
    const trackDropoffIfNeeded = () => {
      if (hasStartedRef.current && !hasSubmittedRef.current) trackFormEvent(FORM_NAME, false);
    };
    window.addEventListener("beforeunload", trackDropoffIfNeeded);
    return () => {
      trackDropoffIfNeeded();
      window.removeEventListener("beforeunload", trackDropoffIfNeeded);
    };
  }, []);

  useEffect(() => {
    document.title = "Support | Truestays";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Get help with your Truestays account, property or booking. Contact our team or browse common questions.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://www.truestays.co.uk/support";
  }, []);

  const set = (f) => (e) => {
    hasStartedRef.current = true;
    setForm((p) => ({ ...p, [f]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    hasSubmittedRef.current = true;
    await base44.functions.invoke("sendLeadEmail", {
      subject: `Support request from ${form.name}`,
      body: `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\nMessage:\n${form.message}`,
    });
    try {
      await base44.functions.invoke("saveLeadToSheet", {
        formData: {
          fullName: form.name,
          email: form.email,
          phone: form.phone,
          mode: "support",
          message: form.message,
        },
        uploadedFiles: [],
      });
    } catch (_) {}
    if (form.email) {
      try {
        await base44.functions.invoke("sendConfirmationEmail", {
          to: form.email,
          name: form.name,
          enquiryType: "support",
          fields: [
            { label: "Name", value: form.name },
            { label: "Email", value: form.email },
            { label: "Phone", value: form.phone },
            { label: "Message", value: form.message },
          ],
        });
      } catch (_) {}
    }
    trackFormEvent(FORM_NAME, true);
    setSubmitting(false);
    setSubmitted(true);
  };

  const focusStyle = (field) =>
    focusedField === field ? { ...inputStyle, borderColor: "hsl(var(--primary))", borderWidth: "1.5px" } : inputStyle;

  return (
    <div style={{ background: "#f4f2f0", minHeight: "100vh" }}>
      {/* Hero bar */}
      <div style={{
        background: "#f4f2f0",
        padding: "100px clamp(20px, 5vw, 80px) 64px",
        textAlign: "center",
      }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>
          Help & Support
        </p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(32px, 4vw, 52px)", color: "#1A1A1A", margin: "0 0 16px", letterSpacing: "-0.03em", lineHeight: 1.08 }}>
          How can we help?
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#555", maxWidth: 480, margin: "0 auto", lineHeight: 1.65 }}>
          Our team is here to help you get the most from your property. Reach out any time.
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 clamp(20px, 5vw, 80px) 80px" }}>

        {/* Main content: form + sidebar */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 28, alignItems: "start", marginTop: 48 }} className="support-grid">

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 16, padding: "36px 32px", border: "1px solid #E5E5E5" }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <MessageCircle size={20} color="#1A1A1A" />
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", margin: 0 }}>Send us a message</h2>
              </div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#888", margin: 0, lineHeight: 1.6 }}>Fill in the form and we'll get back to you within 24 hours.</p>
            </div>

            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <CheckCircle size={48} color="#1A1A1A" style={{ margin: "0 auto 16px", display: "block" }} />
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", marginBottom: 8, marginTop: 0 }}>Message received!</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888", margin: 0, lineHeight: 1.6 }}>A member of our team will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="form-two-col">
                  <div>
                    <label style={labelStyle}>Your name *</label>
                    <input
                      style={focusStyle("name")}
                      required
                      value={form.name}
                      onChange={set("name")}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Email address *</label>
                    <input
                      style={focusStyle("email")}
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Phone number</label>
                  <input
                    style={focusStyle("phone")}
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    onFocus={() => setFocusedField("phone")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="07XXX XXX XXX"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Message *</label>
                  <textarea
                    style={{ ...focusStyle("message"), minHeight: 150, resize: "vertical", lineHeight: 1.6 }}
                    required
                    value={form.message}
                    onChange={set("message")}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Tell us how we can help…"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: "#112215", color: "white", border: "none", borderRadius: 8,
                    padding: "15px", fontFamily: "'DM Sans', sans-serif", fontSize: 15,
                    fontWeight: 600, cursor: "pointer", transition: "background 0.2s", marginTop: 4,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#333"}
                  onMouseLeave={e => e.currentTarget.style.background = "#112215"}
                >
                  <Mail size={16} />
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Valuation redirect */}
            <div style={{ background: "#F4F2EE", borderRadius: 14, padding: "22px 24px", border: "1px solid #E5E5E5" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: "#444", lineHeight: 1.6, margin: "0 0 14px" }}>
                Looking to join our management service or get a valuation? Fill in our <span style={{ fontWeight: 600, color: "#1A1A1A" }}>valuation form</span> instead.
              </p>
              <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, color: "#1A1A1A", textDecoration: "none" }}
                onMouseEnter={e => e.currentTarget.style.color = "hsl(var(--primary))"}
                onMouseLeave={e => e.currentTarget.style.color = "#1A1A1A"}
              >
                Get a free valuation <ChevronRight size={14} />
              </Link>
            </div>

            {/* Quick links */}
            <div style={{ background: "#fff", borderRadius: 14, padding: "22px 24px", border: "1px solid #E5E5E5" }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", marginBottom: 14, marginTop: 0 }}>Quick links</h3>
              {[
                { label: "FAQ", href: "/faqs" },
                { label: "Locations", href: "/locations" },
                { label: "Short-let Management", href: "/" },
                { label: "About", href: "/about" },
                { label: "Interior Service", href: "/interiors" },
                { label: "Resources", href: "/resources" },
              ].map(({ label, href }) => (
                <Link
                  key={label}
                  to={href}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #F0F0F0", textDecoration: "none", color: "#1A1A1A", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}
                  onMouseEnter={e => e.currentTarget.style.color = "hsl(var(--primary))"}
                  onMouseLeave={e => e.currentTarget.style.color = "#1A1A1A"}
                >
                  {label}
                  <ChevronRight size={14} color="#888" />
                </Link>
              ))}
            </div>

            {/* Other ways to get in touch */}
            <div style={{ background: "#fff", borderRadius: 14, padding: "22px 24px", border: "1px solid #E5E5E5" }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", marginBottom: 14, marginTop: 0 }}>Other ways to get in touch</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <a href="mailto:hello@truestays.co.uk" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#F7F7F7", textDecoration: "none", color: "#1A1A1A", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F0F0F0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#F7F7F7"}
                >
                  <Mail size={16} color="#555" />
                  hello@truestays.co.uk
                </a>
                <button onClick={() => setShowBookCall(true)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#F7F7F7", border: "none", cursor: "pointer", color: "#1A1A1A", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F0F0F0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#F7F7F7"}
                >
                  <PhoneCall size={16} color="#555" />
                  Book a call
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .support-grid { grid-template-columns: 1fr !important; }
          .form-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {showBookCall && (
        <BookCallModal
          onClose={() => setShowBookCall(false)}
          onGetValuation={() => { setShowBookCall(false); navigate("/"); }}
        />
      )}
    </div>
  );
}